import { t } from 'i18next';
import { path, equals } from 'ramda';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import useDetectDiffInChanges from '../../../utils/hooks/useDetectDiffInChanges';
import { useForm } from '../../../utils/hooks/useForm';
import { Text, TextInput } from '../common';

import SaveBtn from './common/saveBtn';
import { InputWrapper, DataRowWrapS, SaveButtonWrapper, CompanyRateEditWrapper } from './companyEditS';

const RATE_FIELDS = [
  { textInputName: 'rate.secretary', valuesTextPath: ['rate', 'secretary'] },
  { textInputName: 'rate.auditor', valuesTextPath: ['rate', 'auditor'] },
  { textInputName: 'rate.accountant', valuesTextPath: ['rate', 'accountant'] },
  { textInputName: 'rate.expert', valuesTextPath: ['rate', 'expert'] },
];

const validationSchema = Yup.object().shape({
  rate: Yup.object().shape({
    secretary: Yup.number()
      .typeError(t('companyEdit.errorMessages.rate.mustBeNumber'))
      .max(500, t('companyEdit.errorMessages.rate.maxLimit'))
      .nullable(),
    auditor: Yup.number()
      .typeError(t('companyEdit.errorMessages.rate.mustBeNumber'))
      .max(500, t('companyEdit.errorMessages.rate.maxLimit'))
      .nullable(),
    accountant: Yup.number()
      .typeError(t('companyEdit.errorMessages.rate.mustBeNumber'))
      .max(500, t('companyEdit.errorMessages.rate.maxLimit'))
      .nullable(),
    expert: Yup.number()
      .typeError(t('companyEdit.errorMessages.rate.mustBeNumber'))
      .max(500, t('companyEdit.errorMessages.rate.maxLimit'))
      .nullable(),
  }),
});

const RateSettings = ({ isAdmin, updateCompany, setActiveSettingType }) => {
  const dispatch = useDispatch();
  const company = useSelector((state) => state.company.companyItem);
  const loading = useSelector((state) => state.company.loading);

  const initialValues = {
    rate: {
      secretary: path(['rate', 'secretary'], company) || '',
      auditor: path(['rate', 'auditor'], company) || '',
      accountant: path(['rate', 'accountant'], company) || '',
      expert: path(['rate', 'expert'], company) || '',
    },
  };

  const handleSubmit = (values) => {
    dispatch(updateCompany({ ...company, ...values }, isAdmin));
  };

  const hasChanges = (values) => {
    const initialRateValues = {
      rate: {
        secretary: path(['rate', 'secretary'], company) || '',
        auditor: path(['rate', 'auditor'], company) || '',
        accountant: path(['rate', 'accountant'], company) || '',
        expert: path(['rate', 'expert'], company) || '',
      },
    };
    return !equals(initialRateValues, values);
  };

  const {
    values,
    errors,
    touched,
    onChange: handleChange,
    onBlur: handleBlur,
    onSubmit,
    dirty,
  } = useForm({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  useDetectDiffInChanges(() => hasChanges(values), onSubmit, setActiveSettingType);

  return (
    <CompanyRateEditWrapper>
      <form onSubmit={onSubmit}>
        <p className="rate-description">
          <Text weight="400" color="red">
            {t('companyEdit.rateWarningMsg.desc')}
          </Text>
        </p>
        <DataRowWrapS justify="space-between" wrap="wrap">
          {RATE_FIELDS.map(({ textInputName, valuesTextPath }) => (
            <InputWrapper key={textInputName} width="45%">
              <TextInput
                label={t(`companyEdit.labels.${textInputName}`)}
                name={textInputName}
                type="text"
                onKeyPress={(event) => {
                  const regex = /[0-9]/;
                  if (!regex.test(String.fromCharCode(event.which))) {
                    event.preventDefault();
                  }
                }}
                value={path(valuesTextPath, values)}
                isError={path(valuesTextPath, errors) && path(valuesTextPath, touched)}
                error={path(valuesTextPath, errors)}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </InputWrapper>
          ))}
        </DataRowWrapS>

        <SaveButtonWrapper marginTop="30" marginTopMobile="56">
          <SaveBtn loading={loading} disabled={!dirty && !hasChanges(values)} />
        </SaveButtonWrapper>
      </form>
    </CompanyRateEditWrapper>
  );
};

export default RateSettings;
